import { MutableRefObject, useCallback, useEffect, useRef } from 'react';

import { WebsocketAction } from '@interfaces/webSocketConnectionInfo';

interface IUseConnectionContextSyncProps {
  eventId: string | undefined;
  roomId: string | undefined;
  visitIdRef: MutableRefObject<string | null>;
  isDebug: boolean;
  isWebsocketOpen: boolean;
  sendJSONMessageToWebSocket: (message: unknown) => void;
}

/**
 * Re-states this tab's event context on every websocket it is given.
 *
 * A new row inherits the context from a surviving sibling or not at all (MV-2188),
 * and an empty one silently refuses every billing message for the rest of the visit.
 *
 * @param props.eventId - Experience the tab is in; nothing is sent without one.
 * @param props.roomId - Sent when known: its absence on the row makes the next
 * room message reset the visit's engaged-time clock.
 * @param props.visitIdRef - Read at send time, since it is minted off-render.
 * @param props.isDebug - Debug visits are marked so they stay out of billing.
 * @param props.isWebsocketOpen - Its rising edge is the new-socket signal.
 * @param props.sendJSONMessageToWebSocket - Sender for the action.
 * @returns `syncConnectionContext`, also callable ahead of anything the row gates.
 */
export const useConnectionContextSync = ({
  eventId,
  roomId,
  visitIdRef,
  isDebug,
  isWebsocketOpen,
  sendJSONMessageToWebSocket,
}: IUseConnectionContextSyncProps) => {
  const contextRef = useRef({ eventId, roomId, isDebug });
  const sendRef = useRef(sendJSONMessageToWebSocket);

  useEffect(() => {
    contextRef.current = { eventId, roomId, isDebug };
    sendRef.current = sendJSONMessageToWebSocket;
  }, [eventId, roomId, isDebug, sendJSONMessageToWebSocket]);

  // Read through the ref so this keeps one identity: the billing hook reopens
  // through it, and a new identity per room change would restart its effects.
  const syncConnectionContext = useCallback(() => {
    const context = contextRef.current;

    if (!context.eventId) {
      return;
    }

    sendRef.current({
      action: WebsocketAction.SyncConnectionContext,
      eventId: context.eventId,
      ...(context.roomId ? { roomId: context.roomId } : {}),
      ...(visitIdRef.current ? { visitId: visitIdRef.current } : {}),
      isDebug: context.isDebug,
    });
  }, [visitIdRef]);

  // The rising edge, not the value: a socket that has just replaced another taught
  // the backend nothing, and the one before it took its row with it.
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (!isWebsocketOpen) {
      wasOpenRef.current = false;

      return;
    }

    if (!wasOpenRef.current) {
      wasOpenRef.current = true;
      syncConnectionContext();
    }
  }, [isWebsocketOpen, syncConnectionContext]);

  return syncConnectionContext;
};
